/**
 * Platform sysadmins management — Wave 1a / W1a-22.
 *
 * TanStack Query hooks for the `/api/v1/admin/platform/sysadmins` endpoints.
 * The active sysadmin token (audience `argusai-admin`) is picked up by the
 * shared `apiClient` from `localStorage.getItem('accessToken')` — no special
 * handling needed at the hook level, just hit the path and the audience gate
 * downstream rejects tenant tokens with 401 INVALID_AUDIENCE.
 *
 * Used by:
 *   - `routes/admin/platform/admins.tsx` — list / promote / demote / TOTP rotate
 *   - Password-policy editor in the same page
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import {
  validateStepUpReason,
  SysadminQueryParseError,
} from './use-sysadmin-query';

/**
 * Helper — attach `X-Step-Up-Reason` to a hand-rolled
 * `apiClient.{post,patch,delete}` call. The Administrators hooks
 * predate `createSysadminStepUpMutation` and use custom mutationFns;
 * threading the header is a one-liner via this helper.
 *
 * Throws a `SysadminQueryParseError` (matching the step-up factory's
 * error type) when the reason is out-of-bounds so the UI fails fast
 * with the same UX as the new Platform mutations.
 */
function stepUpInit(stepUpReason: string): { headers: Record<string, string> } {
  const r = validateStepUpReason(stepUpReason);
  if (!r.ok) {
    throw new SysadminQueryParseError(
      r.message ?? 'Invalid step-up reason',
      null,
    );
  }
  return { headers: { 'X-Step-Up-Reason': r.trimmed } };
}

// ─── Wire types ──────────────────────────────────────────────────────────────

export interface SystemAdminRow {
  /** PK of system_admins row — used by DELETE/:id and rotate-totp/:id. */
  id: string;
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
  totpEnrolledAt: string | null;
  lastLoginAt: string | null;
}

export interface SystemAdminListResponse {
  sysadmins: SystemAdminRow[];
  totalActive: number;
  totalInactive: number;
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSymbol: boolean;
  /** Days before mandatory rotation; 0 = no rotation. */
  maxAgeDays: number;
}

// ─── List ────────────────────────────────────────────────────────────────────

export function useSysadmins() {
  return useQuery<SystemAdminListResponse, Error>({
    queryKey: ['platform-sysadmins'],
    queryFn: () => apiClient.get<SystemAdminListResponse>('/admin/platform/sysadmins'),
  });
}

// ─── Promote ─────────────────────────────────────────────────────────────────

// #1136 — Promote is Tier-1 destructive (creates a new sysadmin row).
// The hook now accepts a `stepUpReason` field that is forwarded as the
// X-Step-Up-Reason header on the POST request.
export function usePromoteSysadmin() {
  const qc = useQueryClient();
  return useMutation<
    { sysadmin: SystemAdminRow },
    Error,
    { userId: string; stepUpReason: string }
  >({
    mutationFn: ({ userId, stepUpReason }) =>
      apiClient.post<{ sysadmin: SystemAdminRow }>(
        '/admin/platform/sysadmins',
        { userId },
        stepUpInit(stepUpReason),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-sysadmins'] });
    },
  });
}

// ─── Demote ──────────────────────────────────────────────────────────────────

// #1136 — Demote is Tier-1 destructive (revokes sysadmin role).
export function useDemoteSysadmin() {
  const qc = useQueryClient();
  return useMutation<
    { success: true },
    Error,
    { id: string; stepUpReason: string }
  >({
    mutationFn: ({ id, stepUpReason }) =>
      apiClient.delete<{ success: true }>(
        `/admin/platform/sysadmins/${id}`,
        stepUpInit(stepUpReason),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-sysadmins'] });
    },
  });
}

// ─── Rotate TOTP ─────────────────────────────────────────────────────────────
//
// R6 P0-1 (security): the rotate-totp endpoint now requires a 6-digit
// TOTP code from the ACTING sysadmin (NOT the target). Without this
// step-up MFA, a stolen 30-min access token could rotate every other
// sysadmin's secret and lock them out of the platform.
//
// The dialog (see routes/sysadmin/admins.tsx) collects the code from
// the user and passes it to this mutation. Wire errors:
//   - 401 TOTP_INVALID — caller's code did not verify. UI shows
//     "Code didn't match — please try again." Do NOT show the underlying
//     reason to the user (caller_no_totp_enrolled vs caller_totp_invalid)
//     — leaking that distinguishes provisioning errors from attack
//     attempts.

// #1136 — Rotate TOTP is Tier-1 destructive (invalidates the target
// sysadmin's existing TOTP enrollment). Step-up reason forwarded as
// X-Step-Up-Reason header.
export function useRotateSysadminTotp() {
  return useMutation<
    { qrUri: string; secret: string },
    Error,
    { id: string; totpCode: string; stepUpReason: string }
  >({
    mutationFn: ({ id, totpCode, stepUpReason }) =>
      apiClient.post<{ qrUri: string; secret: string }>(
        `/admin/platform/sysadmins/${id}/rotate-totp`,
        { totpCode },
        stepUpInit(stepUpReason),
      ),
  });
}

// ─── Password policy ─────────────────────────────────────────────────────────

export function usePasswordPolicy() {
  return useQuery<{ policy: PasswordPolicy }, Error>({
    queryKey: ['platform-password-policy'],
    queryFn: () => apiClient.get<{ policy: PasswordPolicy }>(
      '/admin/platform/sysadmins/password-policy'
    ),
  });
}

// #1136 — Password policy PATCH is Tier-1/Tier-2 destructive
// (platform-wide change). The hook accepts a `stepUpReason` field
// forwarded as the X-Step-Up-Reason header.
export function useUpdatePasswordPolicy() {
  const qc = useQueryClient();
  return useMutation<
    { policy: PasswordPolicy },
    Error,
    { patch: Partial<PasswordPolicy>; stepUpReason: string }
  >({
    mutationFn: ({ patch, stepUpReason }) =>
      apiClient.patch<{ policy: PasswordPolicy }>(
        '/admin/platform/sysadmins/password-policy',
        patch,
        stepUpInit(stepUpReason),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-password-policy'] });
    },
  });
}

// ─── Sessions (Phase 5 PR-E) ────────────────────────────────────────────────

/**
 * Wire shape for one row in GET /admin/platform/sysadmins/sessions.
 *
 * The `id` is the refresh_tokens.id — pass it back to POST /:id/revoke
 * to force-revoke. `mfaVerified` is TRUE by definition for sysadmin
 * sessions (the login flow gates refresh issuance on MFA); the column
 * exists in the response so the UI can render the badge without a
 * second lookup. `isActiveSysadmin` flags zombie sessions where the
 * user was demoted but the refresh token hasn't yet expired.
 */
export interface SysadminSessionRow {
  id: string;
  adminId: string;
  adminEmail: string;
  ipAddress: string | null;
  userAgent: string | null;
  mfaVerified: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  expiresAt: string;
  systemAdminId: string | null;
  isActiveSysadmin: boolean;
  cursor: string;
}

export interface SysadminSessionsResponse {
  items: SysadminSessionRow[];
  nextCursor: string | null;
}

export interface SysadminSessionsFilters {
  adminEmail?: string;
  ipAddress?: string;
  cursor?: string;
  limit?: number;
}

/**
 * 10-second polling matches the cadence of useSysadminServices() so
 * the Administrators rail ticks with the rest of the sysadmin
 * operational surface (per use-sysadmin-query.ts SYSADMIN_DEFAULT_REFRESH_MS).
 */
export function useSysadminSessions(filters: SysadminSessionsFilters = {}) {
  const params = new URLSearchParams();
  if (filters.adminEmail) params.set('adminEmail', filters.adminEmail);
  if (filters.ipAddress) params.set('ipAddress', filters.ipAddress);
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();

  return useQuery<SysadminSessionsResponse, Error>({
    queryKey: ['platform-sysadmin-sessions', filters],
    queryFn: () =>
      apiClient.get<SysadminSessionsResponse>(
        `/admin/platform/sysadmins/sessions${qs ? `?${qs}` : ''}`,
      ),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}

// #1136 — Session revoke is Tier-1 destructive (force-logs the
// target sysadmin out). Step-up reason forwarded as X-Step-Up-Reason.
export function useRevokeSysadminSession() {
  const qc = useQueryClient();
  return useMutation<
    { success: true; revokedSessionId: string },
    Error,
    { id: string; stepUpReason: string }
  >({
    mutationFn: ({ id, stepUpReason }) =>
      apiClient.post<{ success: true; revokedSessionId: string }>(
        `/admin/platform/sysadmins/sessions/${id}/revoke`,
        {},
        stepUpInit(stepUpReason),
      ),
    onSuccess: () => {
      // Invalidate the list query so the revoked row disappears
      // immediately. The 10s poll would catch it eventually, but a
      // human-driven revoke deserves an instant UI update.
      qc.invalidateQueries({ queryKey: ['platform-sysadmin-sessions'] });
    },
  });
}

// ─── User search (for Promote dialog) ────────────────────────────────────────

/**
 * Cross-tenant user search for the Promote dialog. The dialog needs to find
 * any user across the platform to promote them to sysadmin. Sysadmin tokens
 * skip RLS via the audience gate so a single platform-wide query works.
 *
 * Wire: `GET /api/v1/admin/platform/users/search?q=<term>` — to be added in
 * a follow-up. For v1 of W1a-22 we use the existing tenant-scoped
 * `GET /users` endpoint with a sysadmin token; that returns 401 because
 * the route doesn't accept the sysadmin audience. So instead the dialog
 * accepts a raw user ID (UUID) for v1 — F-W1a-22.6 captures the proper
 * cross-tenant user-search endpoint.
 */
export function isLikelyUserId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim()
  );
}
